// SHCT-871 — the text-block 크기·문단 spec, in one place.
//
// PRD 10 v2.12 §3.7 is the canonical table and PRD 07 §3 / PRD 11 §3 point at it, because
// 관리지침 · 건강백과 · 상담매뉴얼 share one toolbar. Before this, each editor drove
// `execCommand('formatBlock')` with four paragraph tags and `execCommand('fontSize')` with the
// legacy 1/3/5/7 buckets. Those buckets are the `x-small`~`xxx-large` keywords, which land near
// 10 / 16 / 24 / 48px — 매우 크게 came out three times 기본 and broke the patient app's layout.
// Nothing in any PRD had ever named a pixel value, so there was nothing to be wrong against.
//
// Two things follow from the spec and are easy to get wrong:
//   · The size must be written as `<span style="font-size:Npx; line-height:Mpx">`. `execCommand`
//     emits `<font size="n">`, and `font` is not in the storage allow-list, so the server drops
//     it and the size disappears on save→reload.
//   · 기본(16px) still gets an explicit span. With no tag the patient app falls back to 14px, so
//     "leave it alone, it's already the default" silently shrinks the text.

/** One 본문 size option. `lh` is the 행간 the same row of the table fixes. */
export interface TextSizeOption {
  key: 'small' | 'default' | 'large' | 'xlarge';
  px: number;
  lh: number;
}

// PRD 10 §3.7 「본문 크기 4단계」. Order = the order they appear in the dropdown.
export const TEXT_SIZES: readonly TextSizeOption[] = [
  { key: 'small', px: 14, lh: 20 },
  { key: 'default', px: 16, lh: 24 },
  { key: 'large', px: 18, lh: 28 },
  { key: 'xlarge', px: 20, lh: 30 },
];

export const DEFAULT_TEXT_SIZE = TEXT_SIZES[1];

/**
 * PRD 10 §3.7 「문단 2종」 — 본문 `<p>` and 소제목 `<h2>`.
 *
 * 제목1·제목2·제목3 collapsed into one 소제목: the content title is rendered above the body in
 * the app, so there is no room for an h1 inside the body, and real content used h2 and h3
 * interchangeably as "a subheading mid-article".
 */
export const PARAGRAPH_TAGS = ['p', 'h2'] as const;
export type ParagraphTag = (typeof PARAGRAPH_TAGS)[number];

/** 소제목 has a fixed size, so the size dropdown is disabled while the caret is inside one. */
export const SIZE_LOCKED_TAG: ParagraphTag = 'h2';

export function styleForSize(size: TextSizeOption): string {
  return `font-size: ${size.px}px; line-height: ${size.lh}px`;
}

export function sizeByKey(key: string): TextSizeOption {
  return TEXT_SIZES.find((s) => s.key === key) ?? DEFAULT_TEXT_SIZE;
}

/**
 * Retired `<font size="n">` → the px pair that replaces it.
 *
 * The four values the old toolbar could emit map 1:1 onto the four new options, so existing
 * content converts exactly rather than by guesswork; 2/4/6 were never reachable from the UI and
 * take the nearest neighbour. Used by `normalizeRichHtml` so already-saved blocks stop rendering
 * at the browser's keyword scale the moment they are re-saved.
 */
export const LEGACY_FONT_SIZE_TO_OPTION: Record<string, TextSizeOption> = {
  '1': TEXT_SIZES[0],
  '2': TEXT_SIZES[0],
  '3': TEXT_SIZES[1],
  '4': TEXT_SIZES[2],
  '5': TEXT_SIZES[2],
  '6': TEXT_SIZES[3],
  '7': TEXT_SIZES[3],
};

/**
 * Resolve a selection boundary to the node the caret is visually sitting in.
 *
 * A boundary is a (container, offset) pair, and the container is only a text node when the caret
 * happens to be mid-text. Clicking at the start of a line, or moving there with a key, anchors it
 * on the ELEMENT instead, with the offset pointing at the child it precedes. Walking up from that
 * container starts ABOVE the line's own tag, so the line the author is looking at is never seen.
 *
 * `offset === childNodes.length` is the boundary after the last child, so it resolves to that
 * child rather than past the end.
 */
function nodeAtBoundary(container: Node, offset: number): Node {
  if (container.nodeType !== Node.ELEMENT_NODE) return container;
  const kids = container.childNodes;
  if (kids.length === 0) return container;
  return kids[Math.min(offset, kids.length - 1)];
}

/**
 * Does the selection touch a 소제목?
 *
 * Stops at `root`, so a heading OUTSIDE the editor (a page title, say) can never lock the dropdown.
 *
 * Both halves below exist because the first pass read `startContainer` alone, and QC failed the
 * lock on exactly the two cases that hides:
 *
 *   · a caret placed at the start of a 소제목 line is anchored on the element, not in its text —
 *     `nodeAtBoundary` resolves that before the walk;
 *   · a selection dragged from a 본문 line INTO a 소제목 starts in the 본문, so the walk never
 *     reaches the heading — yet applying a size there would wrap the heading in a font-size span
 *     and give it a size §3.7 fixes. A range that touches a heading at all therefore locks.
 *     §3.7 speaks only of the caret being inside one; extending it to a mixed selection is the
 *     reading that cannot produce a state the spec forbids.
 */
export function isInsideTag(root: HTMLElement, tag: string): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;

  if (!range.collapsed) {
    for (const el of Array.from(root.querySelectorAll(tag))) {
      if (range.intersectsNode(el)) return true;
    }
  }

  let node: Node | null = nodeAtBoundary(range.startContainer, range.startOffset);
  while (node && node !== root) {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === tag) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}

/** Block-level tags the three editors can produce. A size is applied INSIDE each of them. */
const BLOCK_TAGS = 'p,h1,h2,h3,li,blockquote,div';

/**
 * The innermost blocks the range touches, in document order.
 *
 * "Innermost" matters because a `div` wrapping two selected paragraphs also intersects the range;
 * wrapping that would put the size on the container and defeat the per-block split.
 */
function selectedBlocks(root: HTMLElement, range: Range): HTMLElement[] {
  const touched = Array.from(root.querySelectorAll<HTMLElement>(BLOCK_TAGS)).filter((el) =>
    range.intersectsNode(el),
  );
  return touched.filter((el) => !touched.some((other) => other !== el && el.contains(other)));
}

/** The part of `range` that falls inside `block`, so each block is wrapped on its own. */
function clipToBlock(range: Range, block: HTMLElement): Range {
  const clipped = document.createRange();
  clipped.selectNodeContents(block);
  if (block.contains(range.startContainer)) clipped.setStart(range.startContainer, range.startOffset);
  if (block.contains(range.endContainer)) clipped.setEnd(range.endContainer, range.endOffset);
  return clipped;
}

/** Wrap one range in a size span. Returns the span, or null when the range holds nothing. */
function wrapInSizeSpan(range: Range, size: TextSizeOption): HTMLSpanElement | null {
  if (range.collapsed) return null;
  const span = document.createElement('span');
  span.setAttribute('style', styleForSize(size));
  // surroundContents throws when the range straddles element boundaries; extract + append does not.
  span.appendChild(range.extractContents());
  range.insertNode(span);
  return span;
}

/**
 * Which 문단 is the caret sitting in?
 *
 * Drives the 문단 dropdown, so the control names the block the author is actually in rather than
 * the last value they picked. Without it the dropdown still read 소제목 after Enter had started a
 * new 본문 line — and sat next to a 크기 dropdown that had correctly re-enabled itself, so the two
 * halves of the toolbar contradicted each other.
 *
 * `h1`·`h3` are retired but survive in content saved earlier, and §3.7 says they render as 소제목,
 * so they report as 소제목 here too — the dropdown must not offer to "convert" a heading that is
 * already one.
 *
 * NOT the same question as {@link isInsideTag}, and the two must stay separate: this one follows
 * the CARET, while the size lock follows the whole SELECTION (a range dragged from 본문 into a
 * 소제목 locks the size but leaves the caret's own block reported as 본문).
 */
export function paragraphAtCaret(root: HTMLElement): ParagraphTag {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 'p';
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return 'p';

  let node: Node | null = nodeAtBoundary(range.startContainer, range.startOffset);
  while (node && node !== root) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = (node as Element).tagName.toLowerCase();
      if (tag === 'h1' || tag === 'h2' || tag === 'h3') return SIZE_LOCKED_TAG;
      if (tag === 'p') return 'p';
    }
    node = node.parentNode;
  }
  return 'p';
}

/**
 * Apply a 본문 size to the current selection.
 *
 * **The size is applied inside each block separately.** The prototype gets that for free:
 * `rteText.applySize` runs `execCommand('fontSize')`, which the browser applies block by block,
 * and only then swaps each `<font>` for a span. Wrapping the whole range in ONE span instead —
 * which is what this did first — puts `<p>` elements inside an inline `<span>` and leaves an empty
 * block behind, so a selection across two paragraphs came out as
 * `<p></p><span><p>…</p><p>…</p></span><p>…</p>`. Invalid markup, visibly different from the
 * prototype, and the sanitizer would rearrange it again on save.
 *
 * The blocks are processed in reverse document order: extracting from an earlier block would
 * invalidate the offsets the later ranges still hold.
 *
 * 소제목 is skipped — §3.7 fixes its size. The dropdown is already disabled whenever the selection
 * touches one, so this is a guard rather than a path the UI can reach.
 *
 * `styleWithCSS` + `fontSize` would still produce `<font>` in some engines, so the span is written
 * directly rather than through `execCommand`. A collapsed caret has nothing to wrap: an empty span
 * would be unwrapped by `normalizeRichHtml` (attribute-less spans are dropped) and typing after it
 * would inherit nothing, so a zero-width space carries the style until the author types into it.
 * The prototype leaves this case to the browser's pending-style, which its own save-time
 * normalization then rewrites to 기본 — losing the size the author picked.
 *
 * Returns false when there is no usable selection, so the caller can skip the commit.
 */
export function applyTextSize(root: HTMLElement, size: TextSizeOption): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;

  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;

  if (range.collapsed) {
    const span = document.createElement('span');
    span.setAttribute('style', styleForSize(size));
    span.textContent = '\u200b';
    range.insertNode(span);
    const after = document.createRange();
    after.setStart(span.firstChild as Node, 1);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    return true;
  }

  const blocks = selectedBlocks(root, range).filter(
    (b) => b.tagName.toLowerCase() !== SIZE_LOCKED_TAG,
  );

  // No block ancestor to split by (bare text directly in the editor): one span is correct.
  if (blocks.length === 0) {
    const span = wrapInSizeSpan(range, size);
    if (!span) return false;
    sel.removeAllRanges();
    const wrapped = document.createRange();
    wrapped.selectNodeContents(span);
    sel.addRange(wrapped);
    return true;
  }

  const spans: HTMLSpanElement[] = [];
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    const span = wrapInSizeSpan(clipToBlock(range, blocks[i]), size);
    if (span) spans.unshift(span);
  }
  if (spans.length === 0) return false;

  // Leave the same text selected, so a second size click lands on the same words.
  sel.removeAllRanges();
  const after = document.createRange();
  after.setStartBefore(spans[0]);
  after.setEndAfter(spans[spans.length - 1]);
  sel.addRange(after);
  return true;
}

/**
 * Which 본문 size is written at the caret?
 *
 * Reads the nearest ancestor span that declares a `font-size`, because that is the form
 * `applyTextSize` writes and the only one the storage allow-list keeps. Text with no such
 * ancestor reports 기본 — that is what §3.7 calls the default — so the dropdown never claims a
 * size the content does not carry.
 *
 * Used to drive the 크기 dropdown's displayed value. Reporting the applied size rather than
 * resetting to 기본 matters for more than honesty: a `<select>` fires no change event when the
 * option already shown is chosen again, so a dropdown parked on 기본 silently swallowed every
 * click on 기본 — QC saw 기본 "do nothing" and the text stay at the app's 14px fallback.
 */
export function sizeAtCaret(root: HTMLElement): TextSizeOption {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return DEFAULT_TEXT_SIZE;
  const range = sel.getRangeAt(0);
  // Same boundary problem as `isInsideTag`: a caret at the start of a line is anchored on the
  // element, so reading `startContainer` directly walks up from above the size span and reports
  // 기본 for text that carries a size — the dropdown then names an option the text is not at.
  let node: Node | null = nodeAtBoundary(range.startContainer, range.startOffset);
  while (node && node !== root) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const declared = (node as HTMLElement).style?.fontSize;
      const px = declared ? Number.parseFloat(declared) : NaN;
      const match = TEXT_SIZES.find((s) => s.px === px);
      if (match) return match;
    }
    node = node.parentNode;
  }
  return DEFAULT_TEXT_SIZE;
}
